import { API_ROUTES_FOR_AXIOS } from 'src/constants/ApiRoutes';
import { axiosInstance } from 'src/constants/axiosInstance';
import { ApiResponse, PaginatedResponse } from 'src/types/commonResponseTypes';
import {
  DailyRecapGeneralInstructionsSettingsResponse,
  SpaceAutomationPreferenceResponse,
  SpaceAutomationRunSummary,
  SpaceAutomationOverviewResponse,
  SpaceAutomationTestRunResponse,
  SpaceLookupItem,
  SuperAdminSpacesListResponse,
  SpacesResponse,
  TeamFollowThroughOverviewResponse,
  TeamFollowThroughPreferenceState,
  TeamFollowThroughStatusFilter,
  UpdateDailyRecapGeneralInstructionsSettingsPayload,
  UpdateEndOfDayOpenItemsRecapPayload,
  UpdateTeamFollowThroughPreferencePayload,
} from 'src/types/spacesTypes';
import { handleError } from 'src/utils/commonErrorUtils';

export const fetchOrganizationSpaceData = async (
  page: number,
  limit: number,
  targetUserId?: string,
  targetOrganizationId?: string,
  includeAllVisibleSpaces?: boolean,
): Promise<
  ApiResponse<
    PaginatedResponse<SpacesResponse[]> | SuperAdminSpacesListResponse
  >
> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS['getOrganizationSpaceDataAsSuperAdmin'](
          targetUserId,
          page,
          limit,
          targetOrganizationId,
          includeAllVisibleSpaces,
        )
      : API_ROUTES_FOR_AXIOS['getOrganizationSpaceData'](page, limit);

    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateSpaceDetailsById = async (
  spaceId: string,
  updateBody: Partial<SpacesResponse>,
): Promise<ApiResponse<SpacesResponse>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['updateSpaceDetailsById'](spaceId);

    const { data } = await axiosInstance.patch(apiUrl, updateBody);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchOrganizationSpaceLookupData = async (
  page: number,
  limit: number,
  search?: string,
): Promise<ApiResponse<PaginatedResponse<SpaceLookupItem[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS['getOrganizationSpaceLookupData'](
      page,
      limit,
      search,
    );

    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchOrganizationSpaceLookupDataAsSuperAdmin = async (
  userId: string,
  page: number,
  limit: number,
  search?: string,
): Promise<ApiResponse<PaginatedResponse<SpaceLookupItem[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS[
      'getOrganizationSpaceLookupDataAsSuperAdmin'
    ](userId, page, limit, search);

    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchOrganizationMemorySpaceLookupData = async (
  page: number,
  limit: number,
  search?: string,
): Promise<ApiResponse<PaginatedResponse<SpaceLookupItem[]>>> => {
  try {
    const apiUrl = API_ROUTES_FOR_AXIOS.getOrganizationMemorySpaceLookupData(
      page,
      limit,
      search,
    );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchOrganizationMemorySpaceLookupDataAsSuperAdmin = async (
  userId: string,
  page: number,
  limit: number,
  search?: string,
): Promise<ApiResponse<PaginatedResponse<SpaceLookupItem[]>>> => {
  try {
    const apiUrl =
      API_ROUTES_FOR_AXIOS.getOrganizationMemorySpaceLookupDataAsSuperAdmin(
        userId,
        page,
        limit,
        search,
      );
    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchSpaceOpenItemsRecapPreference = async (
  spaceId: string,
  targetUserId?: string,
  targetOrganizationId?: string,
): Promise<ApiResponse<SpaceAutomationPreferenceResponse>> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS['getSuperAdminSpaceOpenItemsRecapPreference'](
          targetUserId,
          spaceId,
          targetOrganizationId,
        )
      : API_ROUTES_FOR_AXIOS['getSpaceOpenItemsRecapPreference'](spaceId);

    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateSpaceOpenItemsRecapPreference = async (
  spaceId: string,
  payload: UpdateEndOfDayOpenItemsRecapPayload,
  targetUserId?: string,
  targetOrganizationId?: string,
): Promise<ApiResponse<SpaceAutomationPreferenceResponse>> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS['updateSuperAdminSpaceOpenItemsRecapPreference'](
          targetUserId,
          spaceId,
          targetOrganizationId,
        )
      : API_ROUTES_FOR_AXIOS['updateSpaceOpenItemsRecapPreference'](spaceId);

    const { data } = await axiosInstance.patch(apiUrl, payload);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchDailyRecapGeneralInstructionsSettings = async (
  targetUserId?: string,
  targetOrganizationId?: string,
): Promise<ApiResponse<DailyRecapGeneralInstructionsSettingsResponse>> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS[
          'getSuperAdminDailyRecapGeneralInstructionsSettings'
        ](targetUserId, targetOrganizationId)
      : API_ROUTES_FOR_AXIOS['getDailyRecapGeneralInstructionsSettings']();

    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchSpaceAutomationOverview = async (
  page = 1,
  limit = 20,
): Promise<ApiResponse<SpaceAutomationOverviewResponse>> => {
  try {
    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS['getSpaceAutomationOverview'](page, limit),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchTeamFollowThroughOverview = async (
  page = 1,
  limit = 10,
  query?: string,
  status: TeamFollowThroughStatusFilter = 'all',
): Promise<ApiResponse<TeamFollowThroughOverviewResponse>> => {
  try {
    const { data } = await axiosInstance.get(
      API_ROUTES_FOR_AXIOS['getTeamFollowThroughOverview'](
        page,
        limit,
        query,
        status,
      ),
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateTeamFollowThroughPreference = async (
  payload: UpdateTeamFollowThroughPreferencePayload,
  idempotencyKey: string,
): Promise<ApiResponse<TeamFollowThroughPreferenceState>> => {
  try {
    const { data } = await axiosInstance.patch(
      API_ROUTES_FOR_AXIOS['updateTeamFollowThroughPreference'](),
      payload,
      {
        headers: {
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': idempotencyKey,
        },
      },
    );

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const updateDailyRecapGeneralInstructionsSettings = async (
  payload: UpdateDailyRecapGeneralInstructionsSettingsPayload,
  targetUserId?: string,
  targetOrganizationId?: string,
): Promise<ApiResponse<DailyRecapGeneralInstructionsSettingsResponse>> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS[
          'updateSuperAdminDailyRecapGeneralInstructionsSettings'
        ](targetUserId, targetOrganizationId)
      : API_ROUTES_FOR_AXIOS['updateDailyRecapGeneralInstructionsSettings']();

    const { data } = await axiosInstance.patch(apiUrl, payload);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const fetchSpaceOpenItemsRecapRuns = async (
  spaceId: string,
  targetUserId?: string,
  targetOrganizationId?: string,
): Promise<ApiResponse<SpaceAutomationRunSummary[]>> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS['getSuperAdminSpaceOpenItemsRecapRuns'](
          targetUserId,
          spaceId,
          targetOrganizationId,
        )
      : API_ROUTES_FOR_AXIOS['getSpaceOpenItemsRecapRuns'](spaceId);

    const { data } = await axiosInstance.get(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const triggerSpaceOpenItemsRecapTestRun = async (
  spaceId: string,
  targetUserId?: string,
  targetOrganizationId?: string,
): Promise<ApiResponse<SpaceAutomationTestRunResponse>> => {
  try {
    const apiUrl = targetUserId
      ? API_ROUTES_FOR_AXIOS['triggerSuperAdminSpaceOpenItemsRecapTestRun'](
          targetUserId,
          spaceId,
          targetOrganizationId,
        )
      : API_ROUTES_FOR_AXIOS['triggerSpaceOpenItemsRecapTestRun'](spaceId);

    const { data } = await axiosInstance.post(apiUrl);

    return data;
  } catch (error) {
    return handleError(error);
  }
};
